import React, { Component } from 'react';
import PropTypes from 'prop-types';

import OutlinedTextField from '../textFields/OutlinedTextField';
import Checkbox from '../Checkbox';
import SectionDivider from '../../shared/SectionDivider';
import AltLoginsForm from '../AltLoginsForm';
import FormButton from '../FormButton';

import { StringConstantsMixin } from '../../../../javascript/application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { loadRecaptcha, executeRecaptcha } from '../../../utils/recaptcha_helpers';
import { validateUserForm } from '../../../utils/form_validaters/user_form_helper';
import { facebookHandler, appleHandler, googleHandler } from '../../../utils/oauth_handlers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { OptimizelyConstants } from '../../../utils/optimizely_helpers';

import baseStyles from '../../../styles/baseStyles.module.scss';
import formStyles from '../styles/styles.module.scss';
import styles from './styles/styles.module.scss';

function trackRegistrationInTatari() {
  // Safety first - Wrap anything Tatari-related in a try/catch
  try {
    if (window.tatari) {
      window.tatari.track('signup');
    }
  } catch (error) {
    console.error('Error tracking registration in Tatari', error);
  }
}

class RegistrationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userRegEmail: '',
      userRegPassword: '',
      userRegFirstName: '',
      userRegLastName: '',
      userEmailSubscribed: props.userEmailSubscribed,
      errors: {
        userRegEmail: '',
        userRegPassword: '',
        userRegFirstName: '',
        userRegLastName: ''
      },
      shownErrors: {},
      serverError: null,
      submitting: false,
      disableSubmit: this.props.enableRecaptcha,
      recaptchaObject: null,
      showErrorsOnType: false
    };
  }

  static REGISTRATION_URL = '/api/alltrails/register';
  static LOGIN_PAGE_URL = '/login';
  static UNITED_STATES_COUNTRY_ID = 313;

  componentDidMount() {
    if (this.props.enableRecaptcha) {
      loadRecaptcha(recaptchaObject => {
        this.setState({ disableSubmit: false, recaptchaObject });
      });
    }
  }

  showEmailSubscribed() {
    return this.props.countryId !== RegistrationForm.UNITED_STATES_COUNTRY_ID;
  }

  handleSubscribedChange = e => {
    e.preventDefault();
    if (!this.showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = this.state;
    this.setState({ userEmailSubscribed: !userEmailSubscribed });
  };

  clearErrors() {
    this.setState({
      errors: {
        userRegEmail: '',
        userRegPassword: '',
        userRegFirstName: '',
        userRegLastName: ''
      },
      serverError: null
    });
  }

  failedFormValidation() {
    let validationErrors = { ...this.state.errors };
    let validation;
    for (let name in this.state.errors) {
      validation = validateUserForm(name, this.state[name], validationErrors);
      validationErrors = { ...validationErrors, ...validation.errors };
    }

    this.setState({ errors: validationErrors });
    return this.hasErrors(validationErrors);
  }

  hasErrors(errors) {
    for (let name in errors) {
      if (errors[name].length > 0) {
        return true;
      }
    }
    return false;
  }
  // handlers

  handleGoogle = response => {
    googleHandler(response, this.props.locale, data => this.registrationSuccessCallback(data, "google"), () => {}, this.registrationComplete);
  };

  handleFacebook = response => {
    facebookHandler(response, this.props.locale, data => this.registrationSuccessCallback(data, "facebook"), () => {}, this.registrationComplete);
  };

  handleLoginClick = () => {
    if (this.props.handleLoginClick) {
      return this.props.handleLoginClick();
    }

    window.location.href = LanguageSupportUtil.wrapUrlSafe(RegistrationForm.LOGIN_PAGE_URL, this.props.locale);
  };

  handleChange = e => {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (this.state.showErrorsOnType) {
      const errors = validateUserForm(name, value, { ...this.state.errors });
      this.setState({ ...errors, [name]: value });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleRedirect() {
    if (!this.props.redirectTo) {
      return;
    }

    window.location.href = this.props.redirectTo;
  }

  registrationSuccessCallback = (data, signupMethod) => {
    if (this.props.handleOptimizelyEvent) {
      this.props.handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_SUCCESS, OptimizelyConstants.OUTCOMES.SUCCESS, { signupMethod });
    }
    if (this.props.handleRegistrationSuccess) {
      this.props.handleRegistrationSuccess(data);
    }

    trackRegistrationInTatari();
    this.clearErrors();
    this.handleRedirect();
  };

  registrationComplete = () => {};

  handleErrorCallback = err => {
    let { errors, serverError } = this.state;
    const serverResponseError = err && err.responseJSON ? err.responseJSON.errors[0] : { message: StringConstantsMixin.strings.ERR_TRY_AGAIN };
    if (serverResponseError && serverResponseError.target === 'email') {
      errors = { ...errors, userRegEmail: serverResponseError.message };
      serverError = serverResponseError.message;
    } else if (serverResponseError && serverResponseError.message) {
      serverError = serverResponseError.message;
    }

    this.setState({ errors, serverError, submitting: false });
    if (this.props.handleOptimizelyEvent) {
      this.props.handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
    }
  };

  submitRegister = recaptchaToken => {
    // API params
    // required =>  :first_name, :last_name, :email, :password
    // optional => :create_session, :subscribed, :recaptcha
    let headers = this.props.locale ? { 'X-Language-Locale': this.props.locale } : {};
    const params = {
      first_name: this.state.userRegFirstName,
      last_name: this.state.userRegLastName,
      email: this.state.userRegEmail,
      password: this.state.userRegPassword,
      create_session: true,
      subscribed: this.state.userEmailSubscribed,
      recaptcha: recaptchaToken
    };

    ServerCommunicationUtil.postApiEndpoint(
      RegistrationForm.REGISTRATION_URL,
      params,
      data => this.registrationSuccessCallback(data, "email"),
      this.handleErrorCallback,
      this.registrationComplete,
      headers
    );
  };

  handleRegister = e => {
    e.preventDefault();
    if (this.state.disableSubmit || this.state.submitting) {
      return;
    }

    if (this.failedFormValidation()) {
      this.setState({ showErrorsOnType: true });
      if (this.props.handleOptimizelyEvent) {
        this.props.handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
      }
      return;
    }

    this.setState({ submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (this.props.enableRecaptcha) {
      executeRecaptcha(this.state.recaptchaObject, 'userSignupLogin').then(
        recaptchaToken => this.submitRegister(recaptchaToken),
        () => this.setState({ submitting: false })
      );
    } else {
      this.submitRegister();
    }
  };

  // renders

  renderFirstNameTextField() {
    return (
      <div className={`${formStyles.inputItem}`} data-test-id="registrationForm-firstNameTextField">
        <OutlinedTextField
          name="userRegFirstName"
          isError={this.state.errors.userRegFirstName.length > 0}
          errorMessage={this.state.errors.userRegFirstName}
          value={this.state.userRegFirstName}
          label={this.props.text.firstName.title}
          changeHandler={e => this.handleChange(e)}
          enterKeyHandler={this.handleRegister}
        />
      </div>
    );
  }

  renderLastNameTextField() {
    return (
      <div className={`${formStyles.inputItem}`} data-test-id="registrationForm-lastNameTextField">
        <OutlinedTextField
          name="userRegLastName"
          isError={this.state.errors.userRegLastName.length > 0}
          errorMessage={this.state.errors.userRegLastName}
          value={this.state.userRegLastName}
          label={this.props.text.lastName.title}
          changeHandler={e => this.handleChange(e)}
          enterKeyHandler={this.handleRegister}
        />
      </div>
    );
  }

  renderEmailTextField() {
    return (
      <div className={`${formStyles.inputItem}`} data-test-id="registrationForm-emailTextFeild">
        <OutlinedTextField
          name="userRegEmail"
          isError={this.state.errors.userRegEmail.length > 0}
          errorMessage={this.state.errors.userRegEmail}
          value={this.state.userRegEmail}
          label={this.props.text.email.title}
          changeHandler={e => this.handleChange(e)}
          enterKeyHandler={this.props.handleRegister}
          type="email"
        />
      </div>
    );
  }

  renderPasswordTextField() {
    return (
      <div
        className={`${formStyles.inputItem} ${this.showEmailSubscribed() ? styles.showSubscribedDistance : ''} `}
        data-test-id="registrationForm-passwordTextFeild"
      >
        <OutlinedTextField
          name="userRegPassword"
          isError={this.state.errors.userRegPassword.length > 0}
          errorMessage={this.state.errors.userRegPassword}
          value={this.state.userRegPassword}
          label={this.props.text.password.title}
          changeHandler={e => this.handleChange(e)}
          enterKeyHandler={this.handleRegister}
          autoComplete={false}
          type="password"
        />
      </div>
    );
  }

  renderEmailOptinCheckbox() {
    if (!this.showEmailSubscribed()) {
      return;
    }

    return (
      <Checkbox
        handleChange={this.handleSubscribedChange}
        checked={this.state.userEmailSubscribed}
        label={StringConstantsMixin.strings.SIGNUP.EMAIL_SUBSCRIBE}
      />
    );
  }

  renderLogin() {
    return (
      <div className={`${baseStyles.action} ${formStyles.prompt} ${baseStyles.center}`}>
        {this.props.text.loginLead}{' '}
        <span className={baseStyles.route} onClick={this.handleLoginClick}>
          {this.props.text.login}
        </span>
      </div>
    );
  }

  renderSignUpBtn() {
    const disabled = this.state.disableSubmit || this.state.submitting;
    return <FormButton value={this.props.text.register} disabled={disabled} data-test-id="registrationForm-signUpButton" />;
  }

  renderFormFields() {
    return (
      <form onSubmit={this.handleRegister}>
        {this.renderFirstNameTextField()}
        {this.renderLastNameTextField()}
        {this.renderEmailTextField()}
        {this.renderPasswordTextField()}
        {this.renderEmailOptinCheckbox()}
        {this.renderServerError()}
        {this.renderSignUpBtn()}
      </form>
    );
  }

  renderServerError() {
    if (!this.state.serverError) {
      return;
    }

    return <div className={`${formStyles.serverError} ${styles.center}`}>{this.state.serverError}</div>;
  }

  render() {
    return (
      <div>
        <div className={baseStyles.section}>
          {this.renderFormFields()}
          {this.renderLogin()}
        </div>
        <SectionDivider>{this.props.text.dividerText}</SectionDivider>
        <AltLoginsForm
          text={this.props.text}
          facebookAppId={this.props.facebookAppId}
          googleClientId={this.props.googleClientId}
          googleLoginHandler={this.handleGoogle}
          facebookLoginHandler={this.handleFacebook}
          appleLoginHandler={appleHandler}
        />
      </div>
    );
  }
}

RegistrationForm.propTypes = {
  enableRecaptcha: PropTypes.bool,
  facebookAppId: PropTypes.number.isRequired,
  googleClientId: PropTypes.string.isRequired,
  locale: PropTypes.string,
  returnTo: PropTypes.string,
  countryId: PropTypes.number,
  text: PropTypes.shape({
    headText: PropTypes.string,
    stepText: PropTypes.string,
    forgotPassword: PropTypes.string,
    register: PropTypes.string,
    email: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    firstName: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    lastName: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    password: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    loginLead: PropTypes.string,
    login: PropTypes.string,
    privacyPolicyLead: PropTypes.string,
    termsLink: PropTypes.string,
    dividerText: PropTypes.string,
    appleLogin: PropTypes.string,
    facebookLogin: PropTypes.string,
    googleLogin: PropTypes.string
  })
};

export default RegistrationForm;
